import { locale, util } from '@glu/core';
import FlexDropdown from '@glu/flex-dropdown';
import FieldSearch from './fieldSearch';

import template from './multiSelectSearch.hbs';

// enable configuration at the system level
// in case certain things need to be different for one application vs another
const defaultConfig = {
  selectedLabel: locale.get('selected'),
  caseSensitive: true,
  multiSelect: true,
  dropdownFilter: true,
  sort: true,
  showCancelIcon: true,
  clearBtn: true,
  disableMultiButton: false
};
const config = defaultConfig;

/**
 * Provides a FlexDropdown list of items that can be used to filter a column of the grid.
 *
 *
 * The following options are accepted via a filterOptions hash when the column is defined for the grid:
 * <ul>
 *     <li>valueField - Optional, The field to use to set the value of each entry on the flexdropdown list created, defaults to the current field multiSelect is setup for.</li>
 *     <li>idField - Optional, The field to use to set the id of each entry on the flexdropdown list created, defaults to the current field multiSelect is setup for. Useful if you have a column with an entity and also have an id for that entity as it can make searching huge grids a little faster.</li>
 *     <li>filterOnField - Optional, The field do the actual filtering on where the id or value is compared against this field for each row.</li>
 *     <li>showAllSelected - Optional, If true, all selected options for the filter will display on the filter badge, regardless of the number of items</li>
 * </ul>
 *
 * Supports the following options, all of which can be set at the application level with requireJs configuration or
 * at the individual level via filterOptions for the column when setting up the grid:
 * <ul>
 *     <li>selectedLabel - Optional, set the text to use for the "N Selected" label that shows when you select more items than will fit on the dropdown selected area. Defaults to "Selected"</li>
 *     <li>caseSensitive - Optional, Boolean, whether the match for the item chosen in the dropdown and the column value during filtering is case sensitive.  Defaults to true.</li>
 *     <li>multiSelect - Optional, Boolean, whether the dropdown allows multiple selections or not. Defaults to true.</li>
 *     <li>dropdownFilter - Optional, Boolean, whether the dropdown includes a filter box where you can type and filter the contents of the dropdown. Defaults to true.</li>
 *     <li>sort - Optional, Boolean, whether the data for the dropdown is sorted prior to being used to create the dropdown. The sort is a simple ascending string based sort.  Defaults to true.</li>
 * </ul>
 *
 * Supports the fol
 *
 * @class MultiSelectSearch
 */
export default FieldSearch.extend(/** @lends MultiSelectSearch.prototype */ {
  template,

  regions: {
    select: '.region-multi-select'
  },

  initialize() {
    this.filterOptions = this.model.get('filterOptions') || {};
    this.dropdownOtpions = util.defaults(util.pick(this.filterOptions, util.keys(defaultConfig)), config);
    util.extend(this, config, util.pick(this.filterOptions, util.keys(defaultConfig)));

    this.filterFields = this.configFields();
    this.dropdown = this.createDropdown();

    this.options.grid.masterCollection.on(`grid:clear:filter:${this.filterFields.filterOnField}`, this.resetFilter);
  },

  onRender() {
    this.showDropdown();
  },

  showDropdown() {
    this.select.show(this.dropdown);
    this.enableSubmitButton();
  },

  onClose() {
    if (this.dropdown) {
      this.dropdown.close();
    }

    this.options.grid.masterCollection.off(`grid:clear:filter:${this.filterFields.filterOnField}`, this.resetFilter);
  },

  resetFilter() {
    this.filterValue = [];
  },

  enableSubmitButton() {
    const hasValue = this.filterValue.length;

    this.ui.$submitBtn.prop('disabled', !hasValue);
    this.ui.$clearBtn.toggleClass('hidden', !hasValue);
  },

  /**
   * Determines the fields to use for the values and ids on the dropdown as well as the field to filter on
   * @returns {{valueField: (valueField|*), idField: idField, filterOnField: (filterOnField|idField|valueField|*)}}
   */
  configFields() {
    const valueField = this.filterOptions.valueField || this.model.get('field');
    const { idField } = this.filterOptions;
    const filterOnField = this.filterOptions.filterOnField || idField || valueField;

    return {
      valueField,
      idField,
      filterOnField
    };
  },

  createDropdown() {
    const dropdownData = [];
    const currentIds = {};
    const { valueField, idField } = this.filterFields;
    const sortData = this.filterOptions.sort !== false;

    this.options.grid.masterCollection.each((item) => {
      const value = (!util.isNullOrUndefined(valueField) || !util.isNaN(valueField)) ? `${item.get(valueField)}` : item.get(valueField);
      const id = idField ? item.get(idField) : value;

      // you cannot filter on nothingness or anything that is "close enough"
      if (!util.isNullOrUndefined(id) && id.length > 0) {
        if (!currentIds[id]) {
          currentIds[id] = true;

          dropdownData.push({
            id,
            name: value
          });
        }
      }
    });

    // sort the data before making the dropdown unless sorting has been explicitly turned off
    if (sortData) {
      this.sortDropdownData(dropdownData);
    }

    const dropdown = new FlexDropdown(util.extend({}, this.dropdownOtpions, {
      data: dropdownData,
      preSelectedNames: this.model.get('value')
    }));

    this.filterValue = this.model.get('value') || [];

    this.listenTo(dropdown, 'selectionChanged:id', this.handleDropdownSelection);

    return dropdown;
  },

  sortDropdownData(dropdownData) {
    dropdownData.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      if (aName === bName) {
        return 0;
      }
      return aName > bName ? 1 : -1;
    });
  },

  handleDropdownSelection(selectedId) {
    if (this.isClosed) {
      return;
    }
    this.filterValue = selectedId;
    this.enableSubmitButton();
  },

  createFilter() {
    return this.buildFilter(this.filterFields.filterOnField, this.filterValue);
  },

  handleSubmit(e) {
    e.preventDefault();

    const filter = this.createFilter();

    try {
      this.trigger('filter', filter);
    } catch (ex) {
      return false;
    }
    return false;
  },

  buildFilter(field, values) {
    let label;

    // if the selected items are showing on the dropdown in the selected area then show them on the filter
    // if "N Selected" is showing because too many are selected then show that on the filter instead
    if (this.filterOptions.showAllSelected || this.dropdown.isSelectedShowing()) {
      label = values.join(', ');
    } else {
      label = `${values.length} ${this.selectedLabel}`;
    }

    this.options.grid.masterCollection.trigger(`grid:filtered:${field}`, values);

    return {
      title: this.model.get('title'),
      value: values,
      label,
      type: this.model.get('type') || 'string',
      field,
      filter: util.bind(this.filter, this, field, this.buildFilterMatches(values))
    };
  },

  buildFilterMatches(values) {
    const caseSensitive = this.filterFields.idField || this.caseSensitive;
    let dropdownValue;
    const filterValues = {};

    for (let i = 0; i < values.length; i++) {
      dropdownValue = values[i];

      if (!caseSensitive && util.isString(dropdownValue)) {
        filterValues[dropdownValue.toLowerCase()] = true;
      } else {
        filterValues[dropdownValue] = true;
      }
    }

    return filterValues;
  },

  filter(field, values, model) {
    return !!values[model.get(field)];
  }
});
